import { Stack, Tooltip, Typography } from "@mui/material";
import { StatusChip, StatusChipColor } from "components/StatusChip";
import { ClockStatus } from "generated-graphql/graphql";
import { ReactElement } from "react";

import { deriveRuleSource } from "./clockRuleSource";
import { CLOCK_STATUS_LABELS } from "./containerDisplay";

/**
 * NONE (citably no applicable limit) takes the neutral role — it isn't a
 * problem state, just the absence of one.
 *
 * Two different "we can't tell" states share the warning role, because they
 * fail on different axes: STATUS_NEEDED is the TIME axis, where an
 * unresolvable generator status means a real deadline may be running unseen;
 * UNDETERMINED is the VOLUME axis, where the area's totals couldn't evaluate
 * some of the waste (mass with no density to convert it), so the cap verdict
 * is unproven rather than passed.
 */
const CLOCK_STATUS_CHIP_COLORS: Record<ClockStatus, StatusChipColor> = {
  [ClockStatus.Ok]: "success",
  [ClockStatus.Approaching]: "warning",
  [ClockStatus.OverLimit]: "error",
  [ClockStatus.Undetermined]: "warning",
  [ClockStatus.Overdue]: "error",
  [ClockStatus.None]: "default",
  [ClockStatus.StatusNeeded]: "warning",
};

export type ChipLimit = { basis: string; citation: string; jurisdiction: string };

/**
 * Explains where a clock reading comes from — which rule, which citation,
 * whose jurisdiction. Wraps whatever renders the reading (the chip, or the
 * days-remaining text in the list's Clock column); with no limits to name it
 * renders its child untouched, so callers don't have to branch.
 */
export function ClockLimitTooltip({
  limits,
  note,
  children,
}: {
  limits: ChipLimit[] | undefined;
  /** An extra line above the limits — what the reading means, when the
   * reading alone doesn't say. One tooltip rather than two, because two on
   * the same element fight each other. */
  note?: string;
  children: ReactElement;
}) {
  const hasLimits = limits !== undefined && limits.length > 0;
  if (!hasLimits && !note) {
    return children;
  }
  return (
    <Tooltip
      title={
        <Stack spacing={0.5}>
          {note && (
            <Typography variant="caption" component="div">
              {note}
            </Typography>
          )}
          {(limits ?? []).map((limit, index) => (
            <Typography key={`${limit.basis}-${index}`} variant="caption" component="div">
              {`${limit.basis} · ${limit.citation} · ${deriveRuleSource(limit).label}`}
            </Typography>
          ))}
        </Stack>
      }
    >
      {/* An element of the tooltip's own to anchor to: neither StatusChip nor
          the text cell forwards a ref. */}
      <span style={{ display: "inline-flex" }}>{children}</span>
    </Tooltip>
  );
}

/**
 * The clock's state, as the light-filled chip the design system specs and the
 * waste frames draw — matching the status chip it sits beside.
 */
export function WasteContainerClockChip({
  status,
  limits,
}: {
  status: ClockStatus;
  /**
   * The limits behind this chip's status, for a hover tooltip explaining
   * which rule produced it. Omitted (or empty) on list screens, which keep
   * the plain chip — only screens that already fetch `clock.limits` pass it.
   */
  limits?: ChipLimit[];
}) {
  return (
    <ClockLimitTooltip limits={limits}>
      <StatusChip
        label={CLOCK_STATUS_LABELS[status]}
        color={CLOCK_STATUS_CHIP_COLORS[status]}
        variant="soft"
      />
    </ClockLimitTooltip>
  );
}
